// src/components/modals/authModal.ts

import { createElement, getElementByIdSafe } from "../../utils/domHelpers";
import { getCookie } from "../../utils/cookies";
import { createLoginForm, createSignupForm } from "../forms/authForms"; // Import form creators
import {
	handleLoginSubmit,
	handleLoginCodeSubmit,
	isUserExistsError,
	handleSignupSubmit,
	getSocialTempMergeQuery,
} from "../../services/authService"; // Import handlers
import { getSocialLoginUrl } from "../../api"; // Import social login URL function
import bootstrap from "../../utils/bootstrap";

// Store instances to prevent duplicates and allow dismissal
let loginOffcanvasInstance: bootstrap.Offcanvas | null = null;
let socialLoginOffcanvasInstance: bootstrap.Offcanvas | null = null;
let registerOffcanvasInstance: bootstrap.Offcanvas | null = null;
let promptOffcanvasInstance: bootstrap.Offcanvas | null = null;
let viewRecordingPromptInstance: bootstrap.Offcanvas | null = null; // ADDED: Instance for the new prompt

const SOCIAL_LOGIN_CANVAS_ID = "login-social-canvas";

// Flag to ensure styles are injected only once
let stylesInjected = false;

/** Injects custom CSS for auth modals if not already present */
function injectAuthModalStyles(): void {
	if (stylesInjected || document.getElementById("auth-modal-styles")) {
		return; // Don't inject multiple times
	}

	const style = createElement("style", { id: "auth-modal-styles" });
	style.textContent = `
        /* Dark theme for auth offcanvas (match other modals/offcanvases) */
        .auth-offcanvas-custom {
            background-color: #212529 !important;
            color: #fff;
            border-top-left-radius: 1rem;
            border-top-right-radius: 1rem;
            transition: transform 0.3s ease-in-out, top 0.3s ease-in-out, left 0.3s ease-in-out, bottom 0.3s ease-in-out, height 0.3s ease-in-out, max-width 0.3s ease-in-out;
        }

        /* Links – match "See All Prizes And Rewards" gold/amber */
        .auth-offcanvas-custom a:not(.btn),
        .auth-offcanvas-custom a:not(.btn):visited {
            color: rgb(230, 154, 6) !important;
            text-decoration: none;
        }
        .auth-offcanvas-custom a:not(.btn):hover {
            color: rgb(230, 154, 6) !important;
            text-decoration: underline;
        }

        /* Social / secondary buttons: dark surface, light text */
        .auth-offcanvas-custom a.btn.btn-light,
        .auth-offcanvas-custom a.btn.btn-light:visited,
        .auth-offcanvas-custom button.btn.btn-light,
        .auth-offcanvas-custom button.btn.btn-light:visited {
            color: #212529 !important;
            background-color: #e9ecef !important;
            border-color: #dee2e6 !important;
            text-decoration: none !important;
        }
        .auth-offcanvas-custom a.btn.btn-light:hover,
        .auth-offcanvas-custom button.btn.btn-light:hover {
            background-color: #dee2e6 !important;
            border-color: #ced4da !important;
        }

        .auth-offcanvas-custom .btn-primary,
        .auth-offcanvas-custom .btn-success {
            color: #fff !important;
        }
        .auth-offcanvas-custom .btn-primary:hover,
        .auth-offcanvas-custom .btn-primary:active,
        .auth-offcanvas-custom .btn-primary:focus,
        .auth-offcanvas-custom .btn-success:hover,
        .auth-offcanvas-custom .btn-success:active,
        .auth-offcanvas-custom .btn-success:focus {
            color: #fff !important;
        }

        .auth-offcanvas-custom .offcanvas-header,
        .auth-offcanvas-custom .offcanvas-body,
        .auth-offcanvas-custom .offcanvas-title,
        .auth-offcanvas-custom h3,
        .auth-offcanvas-custom p,
        .auth-offcanvas-custom .form-label {
            color: #fff;
        }
        .auth-offcanvas-custom .text-secondary,
        .auth-offcanvas-custom .text-secondary a {
            color: #adb5bd !important;
        }
        .auth-offcanvas-custom .text-secondary a:hover {
            color: #ced4da !important;
            text-decoration: underline;
        }

        /* Dark inputs */
        .auth-offcanvas-custom input.form-control,
        .auth-offcanvas-custom input[type="text"],
        .auth-offcanvas-custom input[type="email"],
        .auth-offcanvas-custom input[type="password"] {
            background-color: #343a40 !important;
            color: #fff !important;
            border: 1px solid #495057;
        }
        .auth-offcanvas-custom input.form-control::placeholder {
            color: #adb5bd !important;
        }
        .auth-offcanvas-custom input.form-control:focus {
            background-color: #343a40 !important;
            color: #fff !important;
            border-color: rgb(230, 154, 6);
            box-shadow: 0 0 0 0.25rem rgba(230, 154, 6, 0.25);
        }
        .auth-offcanvas-custom input:-webkit-autofill,
        .auth-offcanvas-custom input:-webkit-autofill:hover,
        .auth-offcanvas-custom input:-webkit-autofill:focus,
        .auth-offcanvas-custom input:-webkit-autofill:active {
            -webkit-box-shadow: 0 0 0 30px #343a40 inset !important;
            -webkit-text-fill-color: #fff !important;
            caret-color: #fff;
            border: 1px solid #495057;
        }

        /* Invert logo for dark background */
        .auth-offcanvas-custom .offcanvas-body > img[alt="Ace Society Logo"] {
            filter: invert(1);
        }


        /* Desktop styles (modal appearance) - using lg breakpoint (992px) */
        @media (min-width: 992px) {
            .auth-offcanvas-custom.offcanvas-bottom {
                /* Override bottom positioning */
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                bottom: auto; /* Reset bottom */
                right: auto; /* Reset right */

                /* Modal dimensions and appearance */
                height: auto !important; /* Adjust height automatically */
                max-height: 90vh; /* Limit maximum height */
                width: 90%; /* Responsive width */
                max-width: 500px; /* Max width for modal */
                border-radius: 0.5rem; /* Standard modal border radius */
                box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15); /* Add shadow */

                 /* Override Bootstrap's transform for offcanvas-bottom */
                 transform: translate(-50%, -50%) !important;
            }

            /* Ensure backdrop is visible for modal */
             .offcanvas-backdrop.show {
                opacity: 0.5;
            }
        }

        /* Register prompt modal - dark theme */
        #register-prompt-canvas.auth-offcanvas-custom {
            background-color: #212529 !important; /* Dark background */
            color: white !important;
        }
        #register-prompt-canvas.auth-offcanvas-custom .offcanvas-header,
        #register-prompt-canvas.auth-offcanvas-custom .offcanvas-body,
        #register-prompt-canvas.auth-offcanvas-custom .offcanvas-title,
        #register-prompt-canvas.auth-offcanvas-custom h3 {
            color: white !important;
        }
        #register-prompt-canvas.auth-offcanvas-custom .text-white {
            color: white !important;
        }

        /* View recording prompt modal - dark theme and ensure it appears above backdrop */
        #view-recording-prompt-canvas.auth-offcanvas-custom {
            background-color: #212529 !important; /* Dark background */
            color: white !important;
        }
        #view-recording-prompt-canvas.auth-offcanvas-custom .offcanvas-header,
        #view-recording-prompt-canvas.auth-offcanvas-custom .offcanvas-body,
        #view-recording-prompt-canvas.auth-offcanvas-custom .offcanvas-title,
        #view-recording-prompt-canvas.auth-offcanvas-custom h3,
        #view-recording-prompt-canvas.auth-offcanvas-custom p {
            color: white !important;
        }
        #view-recording-prompt-canvas.auth-offcanvas-custom .text-secondary {
            color: rgba(255, 255, 255, 0.8) !important;
        }
        #view-recording-prompt-canvas {
            z-index: 9999 !important;
        }
        #view-recording-prompt-canvas.show {
            z-index: 9999 !important;
        }
        #view-recording-prompt-canvas.offcanvas {
            z-index: 9999 !important;
        }
        body:has(#view-recording-prompt-canvas.show) .offcanvas-backdrop {
            z-index: 9998 !important;
            opacity: 0.5 !important;
        }
        body:has(#view-recording-prompt-canvas.show) #view-recording-prompt-canvas {
            position: fixed !important;
        }
        /* Above temp-claim offcanvas (z-index 1090) when opened from save-recording flow */
        #login-canvas,
        #login-canvas.show,
        #login-canvas.offcanvas,
        #login-social-canvas,
        #login-social-canvas.show,
        #login-social-canvas.offcanvas {
            z-index: 1100 !important;
        }
        body:has(#login-canvas.show) .offcanvas-backdrop:last-of-type,
        body:has(#login-social-canvas.show) .offcanvas-backdrop:last-of-type {
            z-index: 1095 !important;
        }

        /* Temp-claim flow: social login fills the viewport (not centered modal) */
        #login-social-canvas.auth-offcanvas-temp-claim-social.offcanvas-bottom {
            top: 0 !important;
            left: 0 !important;
            right: 0 !important;
            bottom: 0 !important;
            transform: none !important;
            width: 100% !important;
            max-width: 100% !important;
            height: 100vh !important;
            height: 100dvh !important;
            max-height: 100dvh !important;
            min-height: 100dvh !important;
            border-radius: 0 !important;
            border-top-left-radius: 0 !important;
            border-top-right-radius: 0 !important;
            box-shadow: none !important;
        }
        #login-social-canvas.auth-offcanvas-temp-claim-social .offcanvas-header {
            padding-top: calc(env(safe-area-inset-top, 0px) + 0.75rem);
        }
        #login-social-canvas.auth-offcanvas-temp-claim-social .offcanvas-body {
            padding-top: 2.5rem !important;
        }
        @media (min-width: 992px) {
            #login-social-canvas.auth-offcanvas-temp-claim-social.offcanvas-bottom {
                top: 0 !important;
                left: 0 !important;
                right: 0 !important;
                bottom: 0 !important;
                transform: none !important;
                width: 100% !important;
                max-width: 100% !important;
                height: 100dvh !important;
                max-height: 100dvh !important;
                border-radius: 0 !important;
                box-shadow: none !important;
            }
        }
    `;
	document.head.appendChild(style);
	stylesInjected = true;
}

/** Removes any existing auth modals/offcanvas elements */
function removeExistingAuthModals(): void {
	const existingLogin = getElementByIdSafe("login-canvas");
	const existingSocialLogin = getElementByIdSafe(SOCIAL_LOGIN_CANVAS_ID);
	const existingRegister = getElementByIdSafe("signup-canvas");
	const existingPrompt = getElementByIdSafe("register-prompt-canvas");
	const existingViewRecordingPrompt = getElementByIdSafe(
		"view-recording-prompt-canvas",
	); // ADDED: Check for new prompt

	// Use the stored instances to hide them properly
	if (loginOffcanvasInstance) {
		try {
			loginOffcanvasInstance.hide();
		} catch (e) {
			console.warn("Error hiding login offcanvas:", e);
		}
	}
	if (socialLoginOffcanvasInstance) {
		try {
			socialLoginOffcanvasInstance.hide();
		} catch (e) {
			console.warn("Error hiding social login offcanvas:", e);
		}
	}
	if (registerOffcanvasInstance) {
		try {
			registerOffcanvasInstance.hide();
		} catch (e) {
			console.warn("Error hiding register offcanvas:", e);
		}
	}
	if (promptOffcanvasInstance) {
		try {
			promptOffcanvasInstance.hide();
		} catch (e) {
			console.warn("Error hiding prompt offcanvas:", e);
		}
	}
	if (viewRecordingPromptInstance) {
		// ADDED: Hide new prompt instance
		try {
			viewRecordingPromptInstance.hide();
		} catch (e) {
			console.warn("Error hiding view recording prompt offcanvas:", e);
		}
	}

	// Explicitly remove elements if they somehow persist after hide
	existingLogin?.remove();
	existingSocialLogin?.remove();
	existingRegister?.remove();
	existingPrompt?.remove();
	existingViewRecordingPrompt?.remove(); // ADDED: Remove new prompt element

	// Nullify instances in the 'hidden' event listener below
}

type CreateAuthOffcanvasOptions = {
	/** Full-screen social login opened from temp-claim save-recording step. */
	tempClaimSocialLogin?: boolean;
	onBackToSaveRecording?: () => void;
};

function returnToSaveRecordingFromSocialLogin(
	onBackToSaveRecording: () => void,
): void {
	const canvas = document.getElementById(SOCIAL_LOGIN_CANVAS_ID);
	const reopenSave = (): void => {
		onBackToSaveRecording();
	};
	if (canvas?.classList.contains("show") && socialLoginOffcanvasInstance) {
		canvas.addEventListener("hidden.bs.offcanvas", reopenSave, {
			once: true,
		});
		socialLoginOffcanvasInstance.hide();
	} else {
		reopenSave();
	}
}

function wireTempClaimSocialCloseButton(
	closeButton: HTMLButtonElement,
	onBackToSaveRecording: () => void,
): void {
	closeButton.removeAttribute("data-bs-dismiss");
	closeButton.setAttribute("aria-label", "Back to save recording");
	const wiredButton = closeButton.cloneNode(true) as HTMLButtonElement;
	wiredButton.addEventListener("click", (e) => {
		e.preventDefault();
		returnToSaveRecordingFromSocialLogin(onBackToSaveRecording);
	});
	closeButton.replaceWith(wiredButton);
}

/**
 * Creates the common structure for an auth offcanvas.
 * @param id - The unique ID for the offcanvas element.
 * @param title - The title text for the offcanvas.
 * @param bodyContent - An array of HTMLElements to place in the body.
 * @returns The created Offcanvas instance.
 */
function createAuthOffcanvas(
	id: string,
	title: string,
	bodyContent: HTMLElement[],
	createOptions: CreateAuthOffcanvasOptions = {},
): bootstrap.Offcanvas | null {
	removeExistingAuthModals(); // Ensure only one is open/being created
	injectAuthModalStyles(); // Ensure styles are present

	const isTempClaimSocialLogin =
		createOptions.tempClaimSocialLogin &&
		id === SOCIAL_LOGIN_CANVAS_ID &&
		createOptions.onBackToSaveRecording;

	const canvas = createElement("div", {
		id: id,
		className: isTempClaimSocialLogin
			? "offcanvas offcanvas-bottom h-100 auth-offcanvas-custom auth-offcanvas-temp-claim-social"
			: "offcanvas offcanvas-bottom h-auto auth-offcanvas-custom",
		attributes: {
			tabindex: "-1",
			"aria-labelledby": `${id}-label`,
			"aria-modal": "true",
			role: "dialog",
			...(isTempClaimSocialLogin
				? { "data-bs-backdrop": "static", "data-bs-keyboard": "false" }
				: {}),
		},
	});

	const canvasHeader = createElement("div", {
		className: "offcanvas-header pb-0 border-0",
	});
	const closeButton = createElement(
		"button",
		{
			className: "btn-close btn-close-white ms-auto",
			attributes: {
				type: "button",
				"data-bs-dismiss": "offcanvas",
				"aria-label": "Close",
			},
		},
		canvasHeader,
	);
	// Make close button white for dark-themed modals
	if (id === "register-prompt-canvas" || id === "view-recording-prompt-canvas") {
		closeButton.classList.add("btn-close-white");
	}
	if (isTempClaimSocialLogin && createOptions.onBackToSaveRecording) {
		wireTempClaimSocialCloseButton(
			closeButton,
			createOptions.onBackToSaveRecording,
		);
	}
	canvas.appendChild(canvasHeader);

	const canvasBody = createElement("div", {
		className: "offcanvas-body text-center pt-0",
	});
	canvas.appendChild(canvasBody);

	const logo = createElement(
		"img",
		{
			attributes: {
				src: "/static/img/acesocietyicon.svg",
				alt: "Ace Society Logo",
				width: "42",
			},
			className: "text-center m-auto d-block mb-2",
			style: {
				filter: "invert(1)",
			},
		},
		canvasBody,
	);
	createElement(
		"h3",
		{ id: `${id}-label`, className: "text-center mt-2 mb-3", textContent: title },
		canvasBody,
	);

	bodyContent.forEach((el) => canvasBody.appendChild(el));

	document.body.appendChild(canvas);

	// Set z-index for view-recording-prompt to ensure it appears above backdrop
	if (id === "view-recording-prompt-canvas") {
		canvas.style.zIndex = "9999";
		canvas.style.position = "fixed";
	}

	try {
		const instance = new bootstrap.Offcanvas(
			canvas,
			isTempClaimSocialLogin
				? { backdrop: "static", keyboard: false, scroll: false }
				: undefined,
		);

		// Ensure view-recording-prompt modal appears above backdrop when shown
		if (id === "view-recording-prompt-canvas") {
			canvas.addEventListener(
				"show.bs.offcanvas",
				() => {
					// Clean up any leftover backdrops from other modals first
					const allBackdrops = document.querySelectorAll(".offcanvas-backdrop");
					allBackdrops.forEach((bd) => {
						const targetSelector = bd.getAttribute("data-bs-target");
						if (targetSelector) {
							const target = document.querySelector(targetSelector);
							// Remove backdrop if its target offcanvas is not showing
							if (!target || !target.classList.contains("show")) {
								bd.remove();
							}
						} else {
							// If no target, check if any offcanvas is showing
							const showingOffcanvas = document.querySelector(".offcanvas.show");
							if (!showingOffcanvas) {
								bd.remove();
							}
						}
					});

					canvas.style.zIndex = "9999";
					canvas.style.position = "fixed";
					// Ensure backdrop is below modal
					const backdrop = document.querySelector(".offcanvas-backdrop");
					if (backdrop) {
						(backdrop as HTMLElement).style.zIndex = "9998";
					}
				},
				{ once: false },
			);
			// Also set on shown event as backup
			canvas.addEventListener(
				"shown.bs.offcanvas",
				() => {
					canvas.style.zIndex = "9999";
					canvas.style.position = "fixed";
					const backdrop = document.querySelector(".offcanvas-backdrop");
					if (backdrop) {
						(backdrop as HTMLElement).style.zIndex = "9998";
					}
					// Final cleanup of any orphaned backdrops
					const allBackdrops = document.querySelectorAll(".offcanvas-backdrop");
					allBackdrops.forEach((bd) => {
						const targetSelector = bd.getAttribute("data-bs-target");
						if (targetSelector) {
							const target = document.querySelector(targetSelector);
							if (!target || !target.classList.contains("show")) {
								bd.remove();
							}
						}
					});
				},
				{ once: false },
			);
		}

		canvas.addEventListener(
			"hidden.bs.offcanvas",
			() => {
				canvas.remove(); // Remove element from DOM
				if (id === "login-canvas") loginOffcanvasInstance = null;
				if (id === SOCIAL_LOGIN_CANVAS_ID) socialLoginOffcanvasInstance = null;
				if (id === "signup-canvas") registerOffcanvasInstance = null;
				if (id === "register-prompt-canvas") promptOffcanvasInstance = null;
				if (id === "view-recording-prompt-canvas")
					viewRecordingPromptInstance = null; // ADDED: Nullify instance
			},
			{ once: true },
		);

		return instance;
	} catch (error) {
		console.error("Failed to initialize Bootstrap Offcanvas:", error);
		canvas.remove();
		return null;
	}
}

// --- Body Creation Functions ---

/** ADDED: Creates the body content for the view recording prompt */
function createViewRecordingPromptBody(): HTMLElement[] {
	const content: HTMLElement[] = [];

	const text = createElement("p", {
		className: "text-white mt-2 mb-4",
		innerHTML:
			"<small>Your recording is ready! Would you like to view it now?</small>",
	});
	content.push(text);

	const buttonContainer = createElement("div", {
		className: "d-flex justify-content-around mt-4",
	});

	const viewBtn = createElement("button", {
		className: "btn btn-primary w-100 me-1",
		textContent: "View Recording",
	});

	viewBtn.addEventListener("click", () => {
		window.location.href = `/dashboard/entries`;
	});

	const notNowBtn = createElement("button", {
		className: "btn btn-secondary w-100 ms-1",
		textContent: "Not Now",
		attributes: { "data-bs-dismiss": "offcanvas" }, // Dismisses the modal
	});

	buttonContainer.append(viewBtn, notNowBtn);
	content.push(buttonContainer);

	return content;
}

/** Creates the body content for the registration prompt */
function createRegisterPromptBody(
	message: string | null = null,
): HTMLElement[] {
	const content: HTMLElement[] = [];

	const text = createElement("p", {
		className: "text-white mt-2 mb-4",
		innerHTML: message
			? message
			: "<small>You're missing out! Register now to save your recording and claim your Ace Society Championship points. Use your points to climb the leaderboard and unlock instant rewards and prizes.</small>",
	});
	content.push(text);

	const buttonContainer = createElement("div", {
		className: "d-flex justify-content-around mt-4",
	});

	const registerBtn = createElement("button", {
		className: "btn btn-primary w-100 me-1",
		textContent: "Register Now",
	});

	registerBtn.addEventListener("click", (e) => {
		e.preventDefault();
		// The `createAuthOffcanvas` called by this function will handle closing the prompt
		showRegisterOffCanvas(window.location.pathname);
	});

	const noThanksBtn = createElement("button", {
		className: "btn btn-secondary w-100 ms-1",
		textContent: "No Thanks",
		attributes: { "data-bs-dismiss": "offcanvas" }, // Dismisses the modal
	});

	buttonContainer.append(registerBtn, noThanksBtn);
	content.push(buttonContainer);

	return content;
}

type LoginOffcanvasOptions = {
	/** Temp users on view recording need is_temp on social login URLs. */
	isTempUser?: boolean;
	/** Prefill email for login form. */
	prefillEmail?: string;
	/** Open email login form immediately. */
	openEmailForm?: boolean;
	/** Show copy that this email already has an account. */
	showExistingAccountNotice?: boolean;
	/**
	 * Guest (temp) user logging in from the entry page: the backend must merge
	 * the temp account's vouchers/entries before issuing the new session, and
	 * reject the login (keeping the guest session) if the merge fails.
	 */
	requireTempMerge?: boolean;
};

type SocialLoginOffcanvasOptions = LoginOffcanvasOptions & {
	/** Return to save-recording step for new-email signup (temp-claim flow only). */
	onBackToSaveRecording?: () => void;
};

function createSocialLoginOffcanvasBody(
	redirectAfterLogin?: string,
	options: SocialLoginOffcanvasOptions = {},
): HTMLElement[] {
	const content: HTMLElement[] = [];
	const redirectUrl = redirectAfterLogin || window.location.pathname;
	const isTemp = options.isTempUser ?? getCookie("is_temporary") === "true";
	const tempQuery = getSocialTempMergeQuery({
		isTempUser: isTemp,
		requireTempMerge: options.requireTempMerge,
	});
	const googleLoginUrl = `${getSocialLoginUrl(
		"google",
	)}?redirect=${encodeURIComponent(redirectUrl)}${tempQuery}`;
	const facebookLoginUrl = `${getSocialLoginUrl(
		"facebook",
	)}?redirect=${encodeURIComponent(redirectUrl)}${tempQuery}`;

	const loginOptionsDiv = createElement("div", {
		className: "d-grid gap-2 mb-3",
		id: "social-login-options",
	});

	const googleBtn = createElement("a", {
		className: "btn btn-light w-100",
		attributes: { role: "button", href: googleLoginUrl },
		innerHTML: '<i class="fa-brands fa-google me-2"></i> Login with Google',
	});

	const facebookBtn = createElement("a", {
		className: "btn btn-light w-100",
		attributes: { role: "button", href: facebookLoginUrl },
		innerHTML: '<i class="fa-brands fa-facebook me-2"></i> Login with Facebook',
	});

	const emailBtn = createElement("button", {
		className: "btn btn-light w-100",
		attributes: { type: "button" },
		innerHTML: '<i class="fa-solid fa-envelope me-2"></i> Login with Email',
	});

	const loginFormContainer = createElement("div", {
		id: "social-login-form-container",
		className: "d-none",
	});

	const setSheetTitle = (text: string): void => {
		const label = document.getElementById(`${SOCIAL_LOGIN_CANVAS_ID}-label`);
		if (label) {
			label.textContent = text;
		}
	};

	const showSocialOptions = (): void => {
		setSheetTitle("Login with social");
		loginOptionsDiv.classList.remove("d-none");
		loginFormContainer.classList.add("d-none");
		loginFormContainer.replaceChildren();
	};

	const showEmailForm = (): void => {
		setSheetTitle("Login with email");
		loginOptionsDiv.classList.add("d-none");
		loginFormContainer.classList.remove("d-none");
		loginFormContainer.replaceChildren();

		const backBtn = createElement("button", {
			className:
				"btn btn-link link-light text-decoration-underline p-0 mb-3 small",
			attributes: { type: "button" },
			textContent: "← Back to login options",
		});
		backBtn.addEventListener("click", showSocialOptions);

		const loginForm = createLoginForm(
			async (username: string, password: string) => {
				await handleLoginSubmit(username, password, redirectUrl, {
					requireTempMerge: options.requireTempMerge,
				});
			},
			{
				textClass: "text-white",
				hideSocialButtons: true,
				redirectUrl,
				isTempUser: isTemp,
				onCodeSubmit: (email: string, code: string) =>
					handleLoginCodeSubmit(email, code, redirectUrl, {
						requireTempMerge: options.requireTempMerge,
					}),
				formId: "social-login-email-form",
			},
		);

		loginFormContainer.append(backBtn, loginForm);
		loginForm
			.querySelector<HTMLInputElement>("#social-login-email-form-username")
			?.focus();
	};

	emailBtn.addEventListener("click", showEmailForm);

	loginOptionsDiv.append(googleBtn, facebookBtn, emailBtn);
	content.push(loginOptionsDiv, loginFormContainer);

	if (options.onBackToSaveRecording) {
		const footerDiv = createElement("div", { className: "mt-3" });
		const backLink = createElement("p", {
			className: "small mb-0",
			innerHTML: 'New here? <a href="#">Save your recording with email</a>',
		});
		backLink.querySelector("a")?.addEventListener("click", (e) => {
			e.preventDefault();
			if (options.onBackToSaveRecording) {
				returnToSaveRecordingFromSocialLogin(options.onBackToSaveRecording);
			}
		});
		footerDiv.appendChild(backLink);
		content.push(footerDiv);
	}

	return content;
}

function createLoginOffcanvasBody(
	redirectAfterLogin?: string,
	options: LoginOffcanvasOptions = {},
): HTMLElement[] {
	const content: HTMLElement[] = [];

	const text = createElement("p", {
		className: "text-secondary mt-2 mb-4",
		innerHTML: "<small>Welcome back!</small>",
	});
	content.push(text);

	if (options.showExistingAccountNotice) {
		const existingAccountNotice = createElement("p", {
			className: "small text-warning text-center mb-3",
			textContent: "We found an existing account for this email. Please log in.",
		});
		content.push(existingAccountNotice);
	}

	const loginOptionsDiv = createElement("div", {
		className: "d-grid gap-2 mb-3",
		id: "login-options-initial",
	});

	const redirectUrl = redirectAfterLogin || window.location.pathname;
	const isTemp = options.isTempUser ?? getCookie("is_temporary") === "true";
	const tempQuery = getSocialTempMergeQuery({
		isTempUser: isTemp,
		requireTempMerge: options.requireTempMerge,
	});
	const googleLoginUrl = `${getSocialLoginUrl(
		"google",
	)}?redirect=${encodeURIComponent(redirectUrl)}${tempQuery}`;
	const facebookLoginUrl = `${getSocialLoginUrl(
		"facebook",
	)}?redirect=${encodeURIComponent(redirectUrl)}${tempQuery}`;

	const googleBtn = createElement("a", {
		className: "btn btn-light w-100",
		attributes: { role: "button", href: googleLoginUrl },
		innerHTML: '<i class="fa-brands fa-google me-2"></i> Login with Google',
	});

	const facebookBtn = createElement("a", {
		className: "btn btn-light w-100",
		attributes: { role: "button", href: facebookLoginUrl },
		innerHTML: '<i class="fa-brands fa-facebook me-2"></i> Login with Facebook',
	});

	const emailBtn = createElement("button", {
		className: "btn btn-light w-100",
		attributes: { type: "button", id: "login-email-btn" },
		innerHTML: '<i class="fa-solid fa-envelope me-2"></i> Login with Email',
	});

	loginOptionsDiv.append(googleBtn, facebookBtn, emailBtn);

	const shouldAutoOpenEmailForm = Boolean(options.openEmailForm);
	if (!shouldAutoOpenEmailForm) {
		content.push(loginOptionsDiv);
	}

	const loginFormContainer = createElement("div", {
		id: "login-form-container",
	});
	content.push(loginFormContainer);

	const showEmailLoginForm = () => {
		loginOptionsDiv.remove();
		const loginForm = createLoginForm(
			async (username: string, password: string) => {
				await handleLoginSubmit(username, password, redirectUrl);
			},
			{
				hideSocialButtons: true,
				redirectUrl,
				isTempUser: isTemp,
				initialEmail: options.prefillEmail,
				onCodeSubmit: (email: string, code: string) =>
					handleLoginCodeSubmit(email, code, redirectUrl),
			},
		);
		loginFormContainer.innerHTML = "";
		loginFormContainer.appendChild(loginForm);
	};

	emailBtn.addEventListener("click", showEmailLoginForm);

	if (shouldAutoOpenEmailForm) {
		showEmailLoginForm();
	}

	const registerLinkDiv = createElement("div", { className: "mt-4" });
	const registerLink = createElement("p", {
		innerHTML: `Don't have an account? <a href="#"><strong>Join here.</strong></a>`,
	});
	registerLink.querySelector("a")?.addEventListener("click", (e) => {
		e.preventDefault();
		showRegisterOffCanvas(redirectAfterLogin);
	});
	registerLinkDiv.appendChild(registerLink);
	content.push(registerLinkDiv);

	return content;
}

function createRegisterOffcanvasBody(
	redirectAfterRegister?: string,
): HTMLElement[] {
	const content: HTMLElement[] = [];

	const text = createElement("p", {
		className: "text-secondary mt-2 mb-4",
		innerHTML: `<small>You're almost there! Just share a few quick details so we can save your recordings and reach you if you win.</small>`,
	});
	content.push(text);

	const signupOptionsDiv = createElement("div", {
		className: "d-grid gap-2 mb-3",
		id: "signup-options-initial",
	});
	const isTempUser = getCookie("is_temporary") === "true";
	const redirectUrl = redirectAfterRegister || window.location.pathname;
	const tempQuery = getSocialTempMergeQuery({ isTempUser });

	const googleSignupUrl = `${getSocialLoginUrl(
		"google",
	)}?redirect=${encodeURIComponent(redirectUrl)}${tempQuery}`;
	const facebookSignupUrl = `${getSocialLoginUrl(
		"facebook",
	)}?redirect=${encodeURIComponent(redirectUrl)}${tempQuery}`;

	const googleBtn = createElement("a", {
		className: "btn btn-light w-100",
		attributes: { role: "button", href: googleSignupUrl },
		innerHTML: '<i class="fa-brands fa-google me-2"></i> Join with Google',
	});

	const facebookBtn = createElement("a", {
		className: "btn btn-light w-100",
		attributes: { role: "button", href: facebookSignupUrl },
		innerHTML: '<i class="fa-brands fa-facebook me-2"></i> Join with Facebook',
	});

	const emailBtn = createElement("button", {
		className: "btn btn-light w-100",
		attributes: { type: "button" },
		innerHTML: '<i class="fa-solid fa-envelope me-2"></i> Join with Email',
	});

	signupOptionsDiv.append(googleBtn, facebookBtn, emailBtn);
	content.push(signupOptionsDiv);

	const signupFormContainer = createElement("div", {
		id: "signup-form-container",
	});
	content.push(signupFormContainer);

	emailBtn.addEventListener("click", () => {
		signupOptionsDiv.remove();
		const signupForm = createSignupForm(
			async (name: string, email: string, password: string) => {
				try {
					await handleSignupSubmit(name, email, password, redirectUrl);
				} catch (error) {
					if (isUserExistsError(error)) {
						showLoginOffCanvas(redirectUrl, {
							isTempUser,
							prefillEmail: email,
							openEmailForm: false,
							showExistingAccountNotice: true,
						});
						return;
					}
					throw error;
				}
			},
		);
		signupFormContainer.innerHTML = "";
		signupFormContainer.appendChild(signupForm);
	});

	const loginLinkDiv = createElement("div", { className: "mt-4" });
	const loginLink = createElement("p", {
		innerHTML: `Already have an account? <a href="#"><strong>Log in</strong></a>`,
	});
	loginLink.querySelector("a")?.addEventListener("click", (e) => {
		e.preventDefault();
		showLoginOffCanvas(redirectAfterRegister);
	});
	loginLinkDiv.appendChild(loginLink);
	content.push(loginLinkDiv);

	const disclaimer = createElement("p", {
		className: "text-secondary mt-4",
		innerHTML: `<small>By registering, you agree to the Ace Society <a href="/terms" class="text-secondary" target="_blank">Terms and Conditions</a>, <a href="/privacy" class="text-secondary" target="_blank">Privacy Policy</a> and <a href="/competition-terms" class="text-secondary" target="_blank">Competition Terms</a>.</small>`,
	});
	content.push(disclaimer);

	return content;
}

// --- Public Functions to Show Modals ---

/** ADDED: Displays the prompt for a temporary user to view their recording. */
export function showViewRecordingPromptOffCanvas(): void {
	if (document.getElementById("view-recording-prompt-canvas")) {
		console.log("View recording prompt canvas already exists.");
		return;
	}

	// Clean up any leftover backdrops from other modals (e.g., inner circle prize modal)
	// This is important because when the inner circle modal stops showing after 3 times,
	// its backdrop might still be in the DOM
	const cleanupLeftoverBackdrops = () => {
		// Remove any backdrops that don't have an associated showing offcanvas
		const backdrops = document.querySelectorAll(".offcanvas-backdrop");
		backdrops.forEach((backdrop) => {
			const targetSelector = backdrop.getAttribute("data-bs-target");
			if (targetSelector) {
				const target = document.querySelector(targetSelector);
				if (!target || !target.classList.contains("show")) {
					backdrop.remove();
				}
			} else {
				// If no target, check if any offcanvas is showing
				const showingOffcanvas = document.querySelector(".offcanvas.show");
				if (!showingOffcanvas) {
					backdrop.remove();
				}
			}
		});

		// Also clean up modal backdrops if no modals are showing
		const showingModal = document.querySelector(".modal.show");
		if (!showingModal) {
			const modalBackdrops = document.querySelectorAll(".modal-backdrop");
			modalBackdrops.forEach((bd) => bd.remove());
		}

		// Reset body classes if nothing is showing
		const showingOffcanvas = document.querySelector(".offcanvas.show");
		if (!showingOffcanvas && !showingModal) {
			document.body.classList.remove("modal-open", "offcanvas-open");
			document.body.style.paddingRight = "";
		}
	};

	// Clean up before showing
	cleanupLeftoverBackdrops();

	const bodyContent = createViewRecordingPromptBody();
	// Only show if content was created (i.e., entry_id cookie exists)
	if (bodyContent.length > 0) {
		viewRecordingPromptInstance = createAuthOffcanvas(
			"view-recording-prompt-canvas",
			"View Your Recording",
			bodyContent,
		);

		// Clean up again after a short delay to catch any backdrops created during initialization
		setTimeout(() => {
			cleanupLeftoverBackdrops();
		}, 100);

		viewRecordingPromptInstance?.show();
	}
}

/** Displays the initial prompt to register. */
export function showRegisterPromptOffCanvas(
	message: string | null = null,
): void {
	if (document.getElementById("register-prompt-canvas")) {
		console.log("Register prompt canvas already exists.");
		return;
	}
	const bodyContent = createRegisterPromptBody(message);
	promptOffcanvasInstance = createAuthOffcanvas(
		"register-prompt-canvas",
		"Create a free account",
		bodyContent,
	);
	promptOffcanvasInstance?.show();
}

/** Displays the Login offcanvas (Google / Facebook / Email). */
export function showLoginOffCanvas(
	redirectAfterLogin?: string,
	options: LoginOffcanvasOptions = {},
): void {
	const existing = document.getElementById("login-canvas");
	if (existing) {
		const instance =
			bootstrap.Offcanvas.getInstance(existing) ?? loginOffcanvasInstance;
		instance?.show();
		return;
	}
	const bodyContent = createLoginOffcanvasBody(redirectAfterLogin, options);
	loginOffcanvasInstance = createAuthOffcanvas(
		"login-canvas",
		"Login to your account",
		bodyContent,
	);
	loginOffcanvasInstance?.show();
}

/** Social-only auth sheet (Google / Facebook + return to email). */
export function showSocialLoginOffCanvas(
	redirectAfterLogin?: string,
	options: SocialLoginOffcanvasOptions = {},
): void {
	const isTempClaimFlow = Boolean(options.onBackToSaveRecording);
	const requireTempMergeMode = String(Boolean(options.requireTempMerge));
	let existing = document.getElementById(SOCIAL_LOGIN_CANVAS_ID);
	if (existing) {
		const isTempClaimMode = existing.classList.contains(
			"auth-offcanvas-temp-claim-social",
		);
		// Login URLs/handlers are baked in at creation, so a sheet built for a
		// different merge mode must be rebuilt rather than reused.
		const mergeModeChanged =
			(existing.dataset.requireTempMerge ?? "false") !== requireTempMergeMode;
		if (isTempClaimFlow !== isTempClaimMode || mergeModeChanged) {
			try {
				bootstrap.Offcanvas.getInstance(existing)?.hide();
			} catch {
				// ignore teardown errors when switching modes
			}
			existing.remove();
			socialLoginOffcanvasInstance = null;
			existing = null;
		}
	}
	if (existing) {
		const instance =
			bootstrap.Offcanvas.getInstance(existing) ?? socialLoginOffcanvasInstance;
		const label = document.getElementById(`${SOCIAL_LOGIN_CANVAS_ID}-label`);
		if (label) {
			label.textContent = "Login with social";
		}
		existing.querySelector("#social-login-options")?.classList.remove("d-none");
		existing
			.querySelector("#social-login-form-container")
			?.classList.add("d-none");
		if (isTempClaimFlow && options.onBackToSaveRecording) {
			const closeButton = existing.querySelector<HTMLButtonElement>(
				".offcanvas-header .btn-close",
			);
			if (closeButton) {
				wireTempClaimSocialCloseButton(
					closeButton,
					options.onBackToSaveRecording,
				);
			}
		}
		instance?.show();
		return;
	}

	const bodyContent = createSocialLoginOffcanvasBody(
		redirectAfterLogin,
		options,
	);
	socialLoginOffcanvasInstance = createAuthOffcanvas(
		SOCIAL_LOGIN_CANVAS_ID,
		"Login with social",
		bodyContent,
		{
			tempClaimSocialLogin: isTempClaimFlow,
			onBackToSaveRecording: options.onBackToSaveRecording,
		},
	);
	const createdCanvas = document.getElementById(SOCIAL_LOGIN_CANVAS_ID);
	if (createdCanvas) {
		createdCanvas.dataset.requireTempMerge = requireTempMergeMode;
	}

	socialLoginOffcanvasInstance?.show();
}

/**
 * Close the temp-claim sheet, then open the social-login offcanvas.
 * Used by “Login with social” on the save-recording step.
 */
export function openLoginOffcanvasFromTempClaim(options: {
	hideTempClaimOffcanvas: () => void;
	getTempClaimOffcanvas?: () => HTMLElement | null;
	reopenTempClaimOffcanvas: () => void;
	redirectAfterLogin?: string;
	/** Set when hide tears down the sheet immediately (no hidden.bs.offcanvas). */
	hideImmediately?: boolean;
}): void {
	const redirect = options.redirectAfterLogin ?? window.location.pathname;
	const isTempUser = getCookie("is_temporary") === "true";

	const openSocial = (): void => {
		showSocialLoginOffCanvas(redirect, {
			isTempUser,
			onBackToSaveRecording: options.reopenTempClaimOffcanvas,
		});
	};

	const tempEl =
		options.getTempClaimOffcanvas?.() ??
		document.getElementById("temp-claim-offcanvas") ??
		document.getElementById("temp-claim-offcanvas-dev-preview") ??
		document.getElementById("temp-claim-offcanvas-dev");

	const waitForHidden =
		!options.hideImmediately && !!tempEl && tempEl.classList.contains("show");

	if (waitForHidden && tempEl) {
		tempEl.addEventListener("hidden.bs.offcanvas", openSocial, { once: true });
		options.hideTempClaimOffcanvas();
		return;
	}

	options.hideTempClaimOffcanvas();
	requestAnimationFrame(openSocial);
}

/** Displays the Register/Signup offcanvas */
export function showRegisterOffCanvas(redirectAfterRegister?: string): void {
	if (document.getElementById("signup-canvas")) {
		console.log("Signup canvas already exists.");
		return;
	}
	const bodyContent = createRegisterOffcanvasBody(redirectAfterRegister);
	registerOffcanvasInstance = createAuthOffcanvas(
		"signup-canvas",
		"Create an account",
		bodyContent,
	);
	registerOffcanvasInstance?.show();
}
